import { fetchBookedSlot, useUpdateBooking } from "$api/client";
import { DATE_FORMAT, DEFAULT_TIME_VALUE, TIME_FORMAT } from "$utils";
import { Button, DatePicker, Form, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import DeleteBooking from "../DeleteBooking/DeleteBooking";
import { useQuery } from "@tanstack/react-query";
import TextArea from "antd/es/input/TextArea";

const UpdateBooking = ({ timeSlots, locationSlot }) => {
  const [showModal, setShowModal] = useState(false);

  const [slot, setSlot] = useState(locationSlot ?? "");
  const [form] = Form.useForm();
  const updateBooking = useUpdateBooking(
    locationSlot.crm_dealercode,
    locationSlot.date,
    locationSlot.team
  );
  const [selectedDate, setSelectedDate] = useState(
    locationSlot?.date ? dayjs(locationSlot.date) : dayjs()
  );

  const { data: currentBooking } = useQuery({
    queryKey: [
      "bookedSlot",
      locationSlot?.crm_dealercode,
      locationSlot?.date,
      locationSlot?.team,
    ],
    queryFn: fetchBookedSlot,
    enabled: !!locationSlot,
  });

  const onFinish = () => {
    const fields = form.getFieldsValue();
    fields["crm_dealercode"] = slot.crm_dealercode;
    fields["team"] = slot.team;
    fields["location_id"] = slot.location_id;
    updateBooking.mutate(fields);
    setShowModal(false);
  };

  const onDateChange = (date) => {
    setSelectedDate(date.tz());
  };

  const onTimeChange = (slot, option) => {
    setSlot(option.slot);
  };

  let bookedSlotsArr = [];

  if (selectedDate && timeSlots && showModal) {
    let slots = timeSlots[selectedDate.format(DATE_FORMAT)];
    if (slots) {
      bookedSlotsArr = slots
        .map((value) => {
          if (!value.booked) {
            return {
              title: dayjs(value.date).format(TIME_FORMAT),
              value: dayjs(value.date).format(TIME_FORMAT),
              slot: value,
            };
          }
          return null;
        })
        .filter((value) => value);
    }
  }

  let time = "";
  if (locationSlot) {
    time = dayjs(locationSlot.date).format(TIME_FORMAT);
  }

  return (
    <>
      <Button
        type="link"
        onClick={() => setShowModal(true)}
        style={{ marginBottom: "-40px" }}
      >
        {time} {currentBooking?.license_plate ?? time}
      </Button>
      {currentBooking && showModal && (
        <Modal
          title="Terminumbuchung"
          open={showModal}
          onCancel={() => setShowModal(false)}
          width={460}
          footer={[
            <DeleteBooking
              key={`deleteAppointment~${currentBooking.id}`}
              crm_dealercode={currentBooking.crm_dealercode}
              team={currentBooking.team}
              date={dayjs(currentBooking.date).format(
                `${DATE_FORMAT}T${TIME_FORMAT}`
              )}
              closeModal={() => setShowModal(false)}
            />,
            <Button
              key={`cancelUpdateAppointment~${currentBooking.id}`}
              size="large"
              onClick={() => setShowModal(false)}
            >
              Abbrechen
            </Button>,
            <Button
              key="updateAppointment"
              form={`updateBooking~${currentBooking.id}`}
              type="primary"
              size="large"
              htmlType="submit"
            >
              Speichern
            </Button>,
          ]}
        >
          <Form
            key={currentBooking}
            name={`updateBooking~${currentBooking.id}`}
            form={form}
            onFinish={onFinish}
            layout="vertical"
          >
            <div style={{ marginBottom: "20px" }}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: "Autokennzeichen kann nicht leer sein!",
                  },
                ]}
                name="license_plate"
                label="Kennzeichen"
                initialValue={currentBooking?.license_plate ?? ""}
              >
                <Input size="large" placeholder="Autokennzeichen" />
              </Form.Item>
            </div>
            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                rules={[
                  {
                    required: true,
                    message: "Datum kann nicht leer sein!",
                  },
                ]}
                name="datePicker"
                initialValue={
                  locationSlot ? dayjs(locationSlot.date) : dayjs().tz()
                }
                label="Datum"
              >
                <DatePicker
                  size="large"
                  onChange={onDateChange}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                style={{ width: "100%" }}
                rules={[
                  {
                    required: true,
                    message: "Uhrzeit kann nicht leer sein!",
                  },
                  {
                    validator: (_, value) => {
                      if (value && value === DEFAULT_TIME_VALUE) {
                        return Promise.reject(
                          "Uhrzeit kann nicht leer oder ungültig sein!"
                        );
                      }
                      return Promise.resolve();
                    },
                    validateTrigger: "onSubmit",
                  },
                ]}
                name="timePicker"
                initialValue={
                  locationSlot?.date
                    ? dayjs(locationSlot.date).format(TIME_FORMAT)
                    : DEFAULT_TIME_VALUE
                }
                label="Zeitslot"
              >
                <Select
                  key={selectedDate}
                  size="large"
                  options={bookedSlotsArr}
                  onChange={onTimeChange}
                />
              </Form.Item>

              <Form.Item style={{ width: "100%" }} label="Bühne">
                <Input
                  key={slot}
                  placeholder="Bühne"
                  size="large"
                  disabled
                  value={slot.team}
                />
              </Form.Item>
            </div>

            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                name="first_name"
                label="Vorname"
                initialValue={currentBooking?.first_name ?? ""}
              >
                <Input placeholder="Vorname" size="large" />
              </Form.Item>
              <Form.Item
                style={{ width: "100%" }}
                name="last_name"
                label="Nachname"
                initialValue={currentBooking?.last_name ?? ""}
              >
                <Input placeholder="Nachname" size="large" />
              </Form.Item>
            </div>

            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                name="phone"
                label="Telefonnummer"
                initialValue={currentBooking?.phone ?? ""}
              >
                <Input placeholder="Telefonnummer" size="large" />
              </Form.Item>
            </div>

            {/* <Space.Compact style={{ marginBottom: "20px" }}>
              <Form.Item
                style={{ width: "20%" }}
                rules={[
                  {
                    required: true,
                    message: "Vorwahlnummer kann nicht leer sein!",
                  },
                ]}
                name="natCode"
                initialValue={
                  currentBooking ? getPhoneIntNumber(currentBooking.phone) : 49
                }
              >
                <Input prefix={"+"} count={count(3)} required />
              </Form.Item>
              <Form.Item
                style={{ width: "20%" }}
                rules={[
                  {
                    required: true,
                    message: "Anbieternummer kann nicht leer sein!",
                  },
                ]}
                name="prefixCode"
                initialValue={
                  currentBooking ? getPhonePrefix(currentBooking.phone) : ""
                }
              >
                <Input count={count(3)} />
              </Form.Item>
              <Form.Item
                style={{ width: "60%" }}
                rules={[
                  { required: true, message: "Nummer kann nicht leer sein!" },
                ]}
                name="number"
                initialValue={
                  currentBooking ? getPhoneNumber(currentBooking.phone) : ""
                }
              >
                <Input count={count(8)} />
              </Form.Item>
            </Space.Compact> */}

            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                name="email"
                label="E-Mail-Adresse"
                initialValue={currentBooking?.email ?? ""}
              >
                <Input placeholder="E-Mail-Adresse" size="large" />
              </Form.Item>
            </div>

            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                rules={[{ required: true, message: "Wählen Sie etwas aus!" }]}
                name="wheels_stored"
                label="Reifen eingelagert?"
                initialValue={currentBooking?.wheels_stored ?? ""}
              >
                <Select
                  size="large"
                  options={[
                    {
                      label: <span>ja</span>,
                      value: true,
                    },
                    {
                      label: <span>nein</span>,
                      value: false,
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item
                style={{ width: "100%" }}
                // rules={[{ required: true, message: "Wählen Sie etwas aus!" }]}
                name="seasonal_check"
                label="Saisonprüfung?"
                initialValue={currentBooking?.seasonal_check ?? null}
              >
                <Select
                  size="large"
                  options={[
                    {
                      label: <span>keine Überprüfung</span>,
                      value: "no_check",
                    },
                    {
                      label: <span>Winterprüfung</span>,
                      value: "winter_check",
                    },
                    {
                      label: <span>Neujahrprüfung</span>,
                      value: "early_check",
                    },
                  ]}
                />
              </Form.Item>
            </div>
            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                name="shelf_number"
                label="Regalnummer"
                initialValue={currentBooking?.shelf_number ?? ""}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item
                style={{ width: "100%" }}
                name="additional_services"
                label="Dienste"
                initialValue={currentBooking?.additional_services ?? ""}
              >
                <Input size="large" />
              </Form.Item>
            </div>
            <div className="timepickers">
              <Form.Item
                style={{ width: "100%" }}
                name="note"
                label="Notizen"
                initialValue={currentBooking?.note ?? ""}
              >
                <TextArea rows={3} size="large" />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default UpdateBooking;
